import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as echarts from 'echarts'
import type { ECharts } from 'echarts'
import ReactECharts from 'echarts-for-react'

import { computeTiming } from '@fit-editor/fit-core'
import type { Activity } from '@fit-editor/fit-core'

import { MoveHorizontal, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { TerrainControls } from '@/components/terrain/terrain-controls'
import { useCurrentActivity } from '@/hooks/use-current-activity'
import { useTerrainOverlay } from '@/hooks/use-terrain-overlay'
import { useThrottled } from '@/hooks/use-throttled'
import { useUnits } from '@/hooks/use-units'
import { getEditedRanges } from '@/lib/edit-ranges'
import { formatDurationSeconds } from '@/lib/format'
import {
  annotationSeriesIds,
  buildHoverMarkLineData,
  buildHoverSeriesPatch,
  buildTimelineOption,
  stackedChartHeightFor,
  stackedPanelCount,
  timelineLegendEntries,
} from '@/lib/timeline-option'
import { describeTerrainResolution, hasAnyGps } from '@/lib/terrain'
import { getAvailableMetrics } from '@/lib/timeline-metrics'
import { getThemeColors } from '@/lib/theme-colors'
import { useActivityStore } from '@/store/activity-store'
import type { LoadedActivity } from '@/store/activity-store'

import { SelectionStatsStrip } from './selection-stats-strip'
import { TimelineLayoutToggle } from './timeline-layout-toggle'
import { TimelineLegend } from './timeline-legend'

const NO_HIDDEN_SERIES: ReadonlySet<string> = new Set()

/** Chart view state carried across a layout-only option rebuild -- see `rememberView`. */
interface PendingView {
  zoom: { start: number; end: number } | null
  /** Legend selection by series name (`false` = toggled off). */
  legend: Map<string, boolean>
  range: { start: number; end: number } | null
}

export interface ActivityTimelineProps {
  loaded: LoadedActivity
  /**
   * Stacked mode: reports the height (px) this component's root would need
   * for every panel to get its preferred plot height -- the measured header
   * rows plus `stackedChartHeightFor(panelCount)` -- so the bottom panel can
   * grow to it by default. `null` when not stacked, not yet measured, or
   * unmounted.
   */
  onPreferredHeightChange?: (heightPx: number | null) => void
}

/**
 * Stacked mode re-lays every panel out (a full option replace) for a new
 * chart height. Height changes after the first are applied this long after
 * they stop -- e.g. once a panel-resize drag pauses -- instead of on every
 * frame of it.
 */
const STACKED_REFIT_DELAY_MS = 120

/** Placeholder fed to every compute function while the real activity is still loading -- see `ActivityDashboard`'s identical constant for why. */
const EMPTY_ACTIVITY: Activity = {
  fileId: undefined,
  records: [],
  laps: [],
  sessions: [],
  events: [],
  activitySummary: undefined,
  otherMessages: {},
  preservedUnknownMessages: [],
  mixedMessageUnknownFields: [],
  decodeErrors: [],
}

/**
 * The bottom-panel timeline: multi-metric chart, zoom/pan, drag-to-select
 * a range (surfaced via the store for a future milestone's crop/split UI
 * to consume), lap/pause/edited-range annotations, and map<->timeline
 * point-hover sync via the shared store state.
 */
export function ActivityTimeline({ loaded, onPreferredHeightChange }: ActivityTimelineProps) {
  const theme = useActivityStore((s) => s.ui.theme)
  const hoveredTimestampMs = useActivityStore((s) => s.hoveredTimestampMs)
  const selectedRangeMs = useActivityStore((s) => s.selectedRangeMs)
  const setHoveredTimestampMs = useActivityStore((s) => s.setHoveredTimestampMs)
  const setSelectedRangeMs = useActivityStore((s) => s.setSelectedRangeMs)
  const splitPickMode = useActivityStore((s) => s.splitPickMode)
  const splitPointsMs = useActivityStore((s) => s.splitPointsMs)
  const addSplitPoint = useActivityStore((s) => s.addSplitPoint)
  const layout = useActivityStore((s) => s.ui.timelineLayout ?? 'combined')
  const units = useUnits()
  const stacked = layout === 'stacked'

  const { result } = useCurrentActivity(loaded.history, { keepPreviousWhileLoading: true })
  const activity = result?.activity ?? EMPTY_ACTIVITY
  const timing = useMemo(() => computeTiming(activity), [activity])
  const editedRanges = useMemo(() => getEditedRanges(loaded.history), [loaded.history])
  const metrics = useMemo(() => getAvailableMetrics(activity.records), [activity.records])
  const hasGps = useMemo(() => hasAnyGps(activity.records), [activity.records])

  // Session-only terrain overlay (not an edit). Only passed to the chart
  // when it's index-aligned with the records being charted -- both are
  // keyed by the same `loaded.history`, the length check is a cheap guard.
  const terrain = useTerrainOverlay(loaded)
  const terrainValues =
    terrain.terrain.visible &&
    terrain.overlay != null &&
    terrain.overlay.values.length === activity.records.length
      ? terrain.overlay.values
      : undefined
  const terrainSource = terrain.overlay != null ? terrain.terrain.source : undefined
  // Selection stats use the loaded overlay even while it's hidden on the chart.
  const terrainValuesForStats =
    terrain.overlay != null && terrain.overlay.values.length === activity.records.length
      ? terrain.overlay.values
      : undefined

  // Measured height of the box the chart fills. Stacked mode divides it
  // among its panels so nothing ever scrolls; combined mode just fills it
  // (its option doesn't depend on the height, so no rebuild there).
  const [chartBoxHeight, setChartBoxHeight] = useState<number | null>(null)
  const measuredHeightRef = useRef<number | null>(null)
  const stackedRef = useRef(stacked)
  useEffect(() => {
    stackedRef.current = stacked
  }, [stacked])

  // Only stacked mode's option depends on the height -- a combined chart
  // must not be fully replaced (losing zoom/legend) just because it resized.
  const stackedBoxHeight = stacked ? chartBoxHeight : null

  const timeBounds = useMemo(() => {
    let min = Infinity
    let max = -Infinity
    for (const record of activity.records) {
      if (!(record.timestamp instanceof Date)) continue
      const t = record.timestamp.getTime()
      if (t < min) min = t
      if (t > max) max = t
    }
    return { min, max }
  }, [activity.records])

  // Stacked-mode legend selection, kept current from `legendselectchanged`
  // and read lazily by the stacked tooltip (see `isSeriesVisible`). Reset on
  // every full option replace below, since `notMerge` resets the legend.
  // A stable mutable Map (not a ref: the option builder receives a reader
  // of it, and refs must not be handed to render-time code).
  const [legendSelected] = useState(() => new Map<string, boolean>())
  const isSeriesVisible = useCallback(
    (name: string) => legendSelected.get(name) !== false,
    [legendSelected],
  )

  // `hoveredTimestampMs` deliberately does NOT drive this option -- it's
  // applied via a separate, merge-mode `setOption` call below instead. This
  // `option` is passed to `<ReactECharts notMerge>`, which fully replaces
  // the chart's internal state (including the user's current legend
  // toggles and dataZoom pan/zoom range) on every change. Rebuilding it on
  // every hover frame -- which used to happen, since `hoveredTimestampMs`
  // was a dependency here -- silently reset both of those on every mouse
  // move: toggle a series off, hover again, it comes back; zoom into a
  // range via the slider, hover again, it snaps back out to the full
  // range. Keeping hover out of this memo's inputs means a full replace
  // only happens when the underlying data genuinely changes.
  const option = useMemo(
    () =>
      buildTimelineOption({
        records: activity.records,
        laps: activity.laps,
        pauses: timing.pauses,
        editedRanges,
        theme,
        hoveredTimestampMs: null,
        minTimeMs: timeBounds.min,
        maxTimeMs: timeBounds.max,
        splitPointsMs,
        terrainValues,
        layout,
        isSeriesVisible,
        units,
        containerHeight: stackedBoxHeight ?? undefined,
      }),
    [
      activity.records,
      activity.laps,
      timing.pauses,
      editedRanges,
      theme,
      timeBounds,
      splitPointsMs,
      terrainValues,
      layout,
      isSeriesVisible,
      units,
      stackedBoxHeight,
    ],
  )

  const echartsInstanceRef = useRef<ECharts | null>(null)

  // The HTML legend's view of which series are toggled off, mirrored from
  // the chart's legend events (incl. the restore below). Tagged with the
  // option it belongs to: a full option replace resets the chart's legend
  // to all-visible without an event, and the tag makes that reset apply
  // here too.
  const optionRef = useRef(option)
  useEffect(() => {
    optionRef.current = option
  }, [option])
  const [legendView, setLegendView] = useState<{
    option: unknown
    hidden: ReadonlySet<string>
  }>({ option: null, hidden: NO_HIDDEN_SERIES })
  const hiddenSeries = legendView.option === option ? legendView.hidden : NO_HIDDEN_SERIES
  const legendEntries = useMemo(() => timelineLegendEntries(option), [option])
  const toggleSeries = useCallback((name: string) => {
    echartsInstanceRef.current?.dispatchAction({ type: 'legendToggleSelect', name })
  }, [])

  const onChartReady = useCallback((instance: ECharts) => {
    echartsInstanceRef.current = instance
  }, [])

  // Applies the hover cursor (plus lap/split markers, which live in the
  // same hidden series) as a targeted, merge-mode update to just that one
  // series -- never touching `legend`/`dataZoom`/anything else, so the
  // user's current toggle and zoom/pan state survives every hover, unlike
  // the full-option replace above. Depending on `option` itself (not just
  // its individual ingredients) means this always re-layers the current
  // hover state on top right after any full replace -- e.g. committing an
  // edit while the cursor is parked mid-chart -- rather than leaving a
  // stale or missing cursor until the next mouse move.
  //
  // Stacked mode has one annotation series per panel; all are patched with
  // the same marker positions (`buildHoverSeriesPatch`).
  useEffect(() => {
    const instance = echartsInstanceRef.current
    if (instance == null) return
    instance.setOption(
      {
        series: buildHoverSeriesPatch(
          annotationSeriesIds(option),
          buildHoverMarkLineData({
            laps: activity.laps,
            hoveredTimestampMs,
            splitPointsMs,
            colors: getThemeColors(theme),
          }),
        ),
      },
      false,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `option` stands in for its own ingredients (activity.laps/theme/splitPointsMs/etc.) so this reapplies right after any full-replace, not just on a direct hover change.
  }, [hoveredTimestampMs, option])

  // A full (notMerge) option replace resets the legend to all-visible and
  // dataZoom to the full range, and drops the brush rectangle. When the
  // replace only re-lays the chart out -- a Combined <-> Stacked switch, or
  // the stacked panels being re-fitted to a new container height -- the
  // view captured just before (`rememberView`) is re-applied here. Runs
  // after echarts-for-react has applied the new option: its
  // componentDidUpdate commits before this parent's passive effect.
  const pendingViewRef = useRef<PendingView | null>(null)
  useEffect(() => {
    legendSelected.clear()
    const view = pendingViewRef.current
    pendingViewRef.current = null
    const instance = echartsInstanceRef.current
    if (view == null || instance == null) return
    if (view.zoom != null) {
      instance.dispatchAction({ type: 'dataZoom', start: view.zoom.start, end: view.zoom.end })
    }
    for (const [name, on] of view.legend) {
      if (on) continue
      instance.dispatchAction({ type: 'legendUnSelect', name })
      legendSelected.set(name, false)
    }
    if (view.range != null) {
      instance.dispatchAction({
        type: 'brush',
        areas: [
          { brushType: 'lineX', xAxisIndex: 0, coordRange: [view.range.start, view.range.end] },
        ],
      })
    }
  }, [option, legendSelected])

  const rememberView = useCallback(() => {
    const instance = echartsInstanceRef.current
    if (instance == null) return
    const dataZoom = (instance.getOption() as { dataZoom?: { start?: number; end?: number }[] })
      .dataZoom?.[0]
    pendingViewRef.current = {
      zoom:
        typeof dataZoom?.start === 'number' && typeof dataZoom.end === 'number'
          ? { start: dataZoom.start, end: dataZoom.end }
          : null,
      legend: new Map(legendSelected),
      range: useActivityStore.getState().selectedRangeMs,
    }
  }, [legendSelected])

  // Root element: its height minus the chart box's is the header rows'
  // ("chrome") height, which the preferred stacked height must include.
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [chromeHeight, setChromeHeight] = useState<number | null>(null)

  // Callback ref: observes the chart box whenever it is (re)mounted.
  const chartBoxRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (element == null || typeof ResizeObserver === 'undefined') return
      let refitTimer: ReturnType<typeof setTimeout> | undefined
      const applyHeight = (height: number) => {
        if (height === measuredHeightRef.current) return
        // Only stacked mode rebuilds its option for a new height.
        if (stackedRef.current && measuredHeightRef.current != null) rememberView()
        measuredHeightRef.current = height
        setChartBoxHeight(height)
      }
      const observer = new ResizeObserver((entries) => {
        const height = Math.round(entries[0]?.contentRect.height ?? 0)
        if (height <= 0) return
        // Read in the same layout pass as `height`, so the two are consistent.
        const rootHeight = rootRef.current?.getBoundingClientRect().height ?? 0
        setChromeHeight(Math.max(0, Math.round(rootHeight - height)))
        clearTimeout(refitTimer)
        if (measuredHeightRef.current == null || !stackedRef.current) {
          applyHeight(height)
        } else {
          refitTimer = setTimeout(() => applyHeight(height), STACKED_REFIT_DELAY_MS)
        }
      })
      observer.observe(element)
      return () => {
        clearTimeout(refitTimer)
        observer.disconnect()
      }
    },
    [rememberView],
  )

  // echarts-for-react's own size sensor doesn't always catch the box growing
  // (e.g. the panel expanding to its stacked default height right after
  // mount), leaving the canvas at its old height while the stacked grids
  // are laid out for the new one -- the lower panels then fall off the
  // canvas. Resize explicitly whenever the measured height or the option
  // changes (runs after the child applied the new option).
  useEffect(() => {
    echartsInstanceRef.current?.resize()
  }, [chartBoxHeight, option])

  const hasChart = metrics.length > 0 || terrainValues != null
  const preferredRootHeight =
    stacked && hasChart && chromeHeight != null
      ? chromeHeight + stackedChartHeightFor(stackedPanelCount(metrics, terrainValues != null))
      : null
  useEffect(() => {
    onPreferredHeightChange?.(preferredRootHeight)
  }, [onPreferredHeightChange, preferredRootHeight])
  useEffect(() => () => onPreferredHeightChange?.(null), [onPreferredHeightChange])

  // Split-point picking (M6): one of the split dialog's three independent
  // ways to choose a point, alongside lap quick-buttons and manual entry.
  // Bound against zrender directly (not ECharts' own `click` event, which
  // only fires for clicks that land on an actual series/graphic element)
  // so a click anywhere in the plot area -- not just exactly on a
  // rendered data point -- adds a split point there.
  useEffect(() => {
    const instance = echartsInstanceRef.current
    if (!splitPickMode || instance == null) return

    const zr = instance.getZr()
    // One grid in combined mode; one per panel in stacked mode -- a click
    // in any panel picks the time under the cursor on that panel's axis.
    const gridCount = Array.isArray(option.grid) ? option.grid.length : 1
    const handleZrClick = (event: unknown) => {
      const { offsetX, offsetY } = event as { offsetX: number; offsetY: number }
      const pixel: [number, number] = [offsetX, offsetY]
      for (let gridIndex = 0; gridIndex < gridCount; gridIndex++) {
        if (!instance.containPixel({ gridIndex }, pixel)) continue
        const timeMs = instance.convertFromPixel({ xAxisIndex: gridIndex }, offsetX) as unknown
        if (typeof timeMs === 'number' && Number.isFinite(timeMs)) {
          addSplitPoint(Math.round(timeMs))
        }
        return
      }
    }
    zr.on('click', handleZrClick)
    return () => zr.off('click', handleZrClick)
  }, [splitPickMode, addSplitPoint, option])

  // Throttled the same way `activity-map.tsx`'s mousemove-driven hover
  // sync is (reusing that exact hook, not a second implementation) --
  // `updateAxisPointer` otherwise fires on nearly every pixel of mouse
  // movement over the chart, each one writing to the shared store and
  // triggering a re-render of both the timeline and the map's hover
  // marker. Clearing hover (`globalout`) stays unthrottled, matching
  // `activity-map.tsx`'s own "clear immediately, throttle only the
  // continuous set" pattern -- the user moving off the chart should
  // never feel delayed.
  const throttledSetHover = useThrottled(setHoveredTimestampMs, 50)

  const syncLegendSelection = useCallback(
    (params: { selected?: Record<string, boolean> }) => {
      legendSelected.clear()
      const hidden = new Set<string>()
      for (const [name, on] of Object.entries(params.selected ?? {})) {
        legendSelected.set(name, on)
        if (!on) hidden.add(name)
      }
      setLegendView({ option: optionRef.current, hidden })
    },
    [legendSelected],
  )

  const onEvents = useMemo(
    () => ({
      updateAxisPointer: (params: { axesInfo?: { axisDim: string; value: number }[] }) => {
        const xAxisInfo = params.axesInfo?.find((info) => info.axisDim === 'x')
        if (xAxisInfo != null) throttledSetHover(xAxisInfo.value)
      },
      globalout: () => setHoveredTimestampMs(null),
      // `legendToggleSelect` (the HTML legend) fires `legendselectchanged`;
      // the view restore's `legendUnSelect` fires `legendunselected`.
      legendselectchanged: syncLegendSelection,
      legendunselected: syncLegendSelection,
      brushSelected: (params: { batch?: { areas: { coordRange?: [number, number] }[] }[] }) => {
        const area = params.batch?.[0]?.areas?.[0]
        if (area?.coordRange == null) {
          setSelectedRangeMs(null)
          return
        }
        const [start, end] = area.coordRange
        setSelectedRangeMs({ start: Math.min(start, end), end: Math.max(start, end) })
      },
    }),
    [throttledSetHover, setHoveredTimestampMs, setSelectedRangeMs, syncLegendSelection],
  )

  // "Select range" arms ECharts' lineX brush (what its toolbox button used
  // to do). A full option replace resets the global cursor, so re-arm it
  // whenever `option` changes.
  const [rangeMode, setRangeMode] = useState(false)
  useEffect(() => {
    echartsInstanceRef.current?.dispatchAction({
      type: 'takeGlobalCursor',
      key: 'brush',
      brushOption: rangeMode ? { brushType: 'lineX', brushMode: 'single' } : false,
    })
  }, [rangeMode, option])

  const clearSelection = useCallback(() => {
    setSelectedRangeMs(null)
    echartsInstanceRef.current?.dispatchAction({ type: 'brush', areas: [] })
  }, [setSelectedRangeMs])

  if (result == null) {
    return (
      <p className="text-muted-foreground flex h-full items-center justify-center text-sm">
        Loading timeline…
      </p>
    )
  }

  if (activity.records.length === 0) {
    return (
      <p className="text-muted-foreground flex h-full items-center justify-center text-sm">
        No time-series data in this activity.
      </p>
    )
  }

  return (
    <div ref={rootRef} className="flex h-full min-w-0 flex-col gap-1">
      {splitPickMode && (
        <p
          role="status"
          className="border-warning bg-warning/10 text-warning-text rounded-md border px-2 py-1 text-xs"
        >
          Click anywhere on the chart to add a split point there ({splitPointsMs.length} added so
          far).
        </p>
      )}
      {/* Three columns so the layout/terrain controls stay centred whether or not a selection is shown on the right; on phones one wrapping row with icon-only buttons, leaving the height to the chart. */}
      <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <p className="text-muted-foreground min-w-0">
          {activity.laps.length} lap{activity.laps.length === 1 ? '' : 's'} &middot;{' '}
          {timing.pauses.length} pause{timing.pauses.length === 1 ? '' : 's'}
          {editedRanges.length > 0 &&
            ` · ${editedRanges.length} edited range${editedRanges.length === 1 ? '' : 's'}`}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <TimelineLayoutToggle onBeforeChange={rememberView} />
          <Button
            variant={rangeMode ? 'default' : 'outline'}
            size="sm"
            className="max-sm:w-8 max-sm:px-0"
            aria-pressed={rangeMode}
            title="Drag on the chart to select a time range"
            onClick={() => setRangeMode((on) => !on)}
          >
            <MoveHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="max-sm:sr-only">Select range</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="max-sm:w-8 max-sm:px-0"
            title="Clear the selected time range"
            disabled={selectedRangeMs == null}
            onClick={clearSelection}
          >
            <X className="h-3.5 w-3.5 sm:hidden" aria-hidden="true" />
            <span className="max-sm:sr-only">Clear selection</span>
          </Button>
          <TerrainControls activityId={loaded.id} hasGps={hasGps} terrain={terrain} />
        </div>
        {selectedRangeMs == null ? (
          <div aria-hidden="true" className="hidden sm:block" />
        ) : (
          <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
            <span className="text-foreground font-medium">
              Selected:{' '}
              {formatDurationSeconds((selectedRangeMs.end - selectedRangeMs.start) / 1000)} (
              {new Date(selectedRangeMs.start).toLocaleTimeString()} &ndash;{' '}
              {new Date(selectedRangeMs.end).toLocaleTimeString()})
            </span>
          </div>
        )}
      </div>
      {selectedRangeMs != null && (
        <SelectionStatsStrip
          activity={activity}
          range={selectedRangeMs}
          terrainValues={terrainValuesForStats}
        />
      )}

      {metrics.length === 0 && terrainValues == null ? (
        <p className="text-muted-foreground flex flex-1 items-center justify-center text-sm">
          No chartable metrics (heart rate, speed, cadence, power, altitude, temperature) were found
          in this activity&apos;s records.
        </p>
      ) : (
        <>
          <span className="sr-only">
            Line chart over time of{' '}
            {[
              ...metrics.map((m) => m.label),
              ...(terrainValues != null ? ['terrain elevation'] : []),
            ].join(', ')}
            , with {activity.laps.length} lap boundary marker{activity.laps.length === 1 ? '' : 's'}{' '}
            and {timing.pauses.length} paused interval{timing.pauses.length === 1 ? '' : 's'} shaded
            {stacked ? ', one panel per metric' : ''}. Use the slider beneath the chart, or scroll
            while hovering over it, to zoom; use the Select range button above the chart, then drag
            on the chart, to select a time range.
          </span>
          {/*
            Same element in both layouts, so switching never remounts the
            chart -- the ECharts instance, and with it the view re-applied
            above, survive the switch. The chart always fills this box
            exactly (stacked panels are fitted to its measured height), so
            nothing scrolls; overflow-hidden + min-w-0 let it shrink with the
            window before ECharts' own resize catches up.
          */}
          <TimelineLegend entries={legendEntries} hidden={hiddenSeries} onToggle={toggleSeries} />
          <div ref={chartBoxRef} className="min-h-0 min-w-0 flex-1 overflow-hidden">
            <ReactECharts
              echarts={echarts}
              option={option}
              onEvents={onEvents}
              onChartReady={onChartReady}
              notMerge
              style={{ height: '100%', width: '100%' }}
              aria-hidden="true"
            />
          </div>
        </>
      )}
      {terrainSource != null && (
        <p className="text-muted-foreground text-xs">
          Terrain elevation (dashed):{' '}
          {terrain.terrain.description ?? describeTerrainResolution(terrainSource)} &middot;
          ground-surface model, not written to your file &middot;{' '}
          <a
            href="https://mapterhorn.com/attribution"
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground underline underline-offset-2"
          >
            © Mapterhorn
          </a>
          {!terrain.terrain.visible && ' · hidden'}
        </p>
      )}
    </div>
  )
}
